import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Trophy, Star, Flame, Heart, Shield, Zap, Crown, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';

/**
 * AchievementUnlocked — Full-screen celebration with confetti, sound, and animations.
 * Listens for 'hibff:achievement' custom events from anywhere in the app.
 * Also polls the milestones API for unseen achievements on mount.
 */

const ACHIEVEMENT_TYPES = {
  first_conversation: {
    icon: Sparkles, title: 'First Conversation!', subtitle: 'You just had your first practice session. This is where it begins.',
    gradient: 'from-violet-500 to-purple-700', confettiColors: ['#8B5CF6', '#A78BFA', '#7C3AED', '#DDD6FE'],
  },
  ten_conversations: {
    icon: Flame, title: '10 Conversations!', subtitle: "You're building a real habit. Ten sessions and counting!",
    gradient: 'from-amber-500 to-orange-600', confettiColors: ['#F59E0B', '#FBBF24', '#F97316', '#FDE68A'],
  },
  hundred_hearts: {
    icon: Heart, title: '100 Hearts Earned!', subtitle: "A hundred hearts — you're genuinely connecting.",
    gradient: 'from-red-500 to-rose-600', confettiColors: ['#EF4444', '#F87171', '#FB7185', '#FECDD3'],
  },
  first_badge: {
    icon: Trophy, title: 'First Badge!', subtitle: 'Your first achievement badge is here. Collect them all!',
    gradient: 'from-emerald-500 to-green-600', confettiColors: ['#10B981', '#34D399', '#059669', '#D1FAE5'],
  },
  all_foundation: {
    icon: Crown, title: 'Foundation Complete!', subtitle: "All Foundation badges earned. You've built a rock-solid base.",
    gradient: 'from-indigo-500 to-blue-700', confettiColors: ['#6366F1', '#818CF8', '#4F46E5', '#C7D2FE'],
  },
  first_streak_week: {
    icon: Zap, title: '7-Day Streak!', subtitle: 'A full week of showing up. Consistency builds confidence.',
    gradient: 'from-orange-500 to-red-500', confettiColors: ['#F97316', '#FB923C', '#EF4444', '#FFEDD5'],
  },
  thirty_day_streak: {
    icon: Star, title: '30-Day Streak!', subtitle: 'Thirty days straight. You are unstoppable.',
    gradient: 'from-yellow-500 to-amber-600', confettiColors: ['#EAB308', '#FACC15', '#F59E0B', '#FEF3C7'],
  },
  first_nft: {
    icon: Shield, title: 'First NFT Minted!', subtitle: 'Your achievement is on the blockchain. Truly yours forever.',
    gradient: 'from-pink-500 to-fuchsia-600', confettiColors: ['#EC4899', '#F472B6', '#D946EF', '#FCE7F3'],
  },
  profile_complete: {
    icon: Star, title: 'Profile Complete!', subtitle: '+50 Hearts earned. You\'re ready for the full HiBFF experience.',
    gradient: 'from-teal-500 to-cyan-600', confettiColors: ['#14B8A6', '#2DD4BF', '#06B6D4', '#CCFBF1'],
  },
  first_challenge: {
    icon: Zap, title: 'First Challenge Done!', subtitle: 'You completed a real-world challenge. Practice makes confident.',
    gradient: 'from-lime-500 to-green-600', confettiColors: ['#84CC16', '#A3E635', '#22C55E', '#ECFCCB'],
  },
};

const SOUND_URL = 'data:audio/wav;base64,UklGRl4FAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YToFAACAgICAgICAgICHj5eTj4WAgIKLlpqWjIOAgIiUnJiOgoCAh5KcmY+DgICFj5qZkYWAgIWPlpmRhYCAhI+amZGFgICEjpmYkoWBgISNmJeThoGAg42Yl5OGgYCDjJeXlIaBgIOLlpaUh4KAg4uVlZSIgoGCi5SUlImCgYKKk5OUiYOBgoqSkpOKg4GCiZGRk4qEgoGJkJCTi4SCgYmPj5KLhIKBiI6OkoyFgoGIjY2RjIWDgYiMjJCMhoOBh4uLj42Gg4GHiouOjYeEgYeJio2NiISBh4iJjI2IhIGGiImLjImFgYaHiIqMiYWChoaHiYuKhoKBhYaHiIuKh4OBhYWGh4qKh4OBhYSFhouKiIOChYSEhYqJiYSChIOEhYmJiYSChIKDhIiJiYWDg4KDg4eIiYWDg4GCg4aIiIaEg4GBgoWHiIaEhIGBgoSGh4aFhIGBgYOFhoeGhYKBgYKEhYaGhYOCgYGCg4WFhoWDg4GBgoOEhYWFhIOCgYGCg4SEhYWEg4KBgYGCA==';

function fireConfetti(colors) {
  const duration = 2500;
  const end = Date.now() + duration;

  const frame = () => {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
      zIndex: 10000,
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
      zIndex: 10000,
    });

    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();

  // Big burst in the center
  setTimeout(() => {
    confetti({
      particleCount: 80,
      spread: 100,
      origin: { x: 0.5, y: 0.4 },
      colors,
      zIndex: 10000,
      scalar: 1.2,
    });
  }, 300);
}

function playSound() {
  try {
    const audio = new Audio(SOUND_URL);
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch {}
}

const AchievementUnlocked = () => {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | entering | visible | exiting
  const timeoutRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Listen for custom achievement events
  useEffect(() => {
    const handler = (e) => {
      const type = e.detail?.type;
      const custom = e.detail?.custom;
      if (custom) {
        setQueue(prev => [...prev, { ...custom, _custom: true }]);
      } else if (type && ACHIEVEMENT_TYPES[type]) {
        setQueue(prev => [...prev, { type, ...ACHIEVEMENT_TYPES[type] }]);
      }
    };
    window.addEventListener('hibff:achievement', handler);
    return () => window.removeEventListener('hibff:achievement', handler);
  }, []);

  // Poll milestones API on mount
  useEffect(() => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    if (!token) return;
    fetch(`${API_URL}/api/engagement/milestones`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.unseen_celebrations?.length > 0) {
          const achievements = data.unseen_celebrations
            .map(c => ACHIEVEMENT_TYPES[c.milestone_type] ? { type: c.milestone_type, ...ACHIEVEMENT_TYPES[c.milestone_type] } : null)
            .filter(Boolean);
          if (achievements.length > 0) setQueue(prev => [...prev, ...achievements]);
        }
      }).catch(() => {});
  }, [API_URL]);

  const dismiss = useCallback((achievement) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setPhase('exiting');

    // Mark as seen on backend
    if (achievement?.type) {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      if (token) {
        fetch(`${API_URL}/api/engagement/milestones/${achievement.type}/seen`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    }

    setTimeout(() => {
      setCurrent(null);
      setPhase('idle');
    }, 500);
  }, [API_URL]);

  // Process queue — declared after `dismiss` so we can safely add it to deps.
  useEffect(() => {
    if (phase !== 'idle' || queue.length === 0) return;
    const next = queue[0];
    setQueue(prev => prev.slice(1));
    setCurrent(next);
    setPhase('entering');

    // Trigger confetti + sound
    setTimeout(() => {
      fireConfetti(next.confettiColors || ['#F5831F', '#8B5CF6', '#FBBF24', '#10B981']);
      playSound();
      setPhase('visible');
    }, 100);

    // Auto-dismiss after 5 seconds
    timeoutRef.current = setTimeout(() => dismiss(next), 5000);
  }, [phase, queue, dismiss]);

  if (!current) return null;

  const Icon = current.icon || Trophy;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-500 ${
        phase === 'entering' || phase === 'exiting' ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
      }`}
      data-testid="achievement-unlocked"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={() => dismiss(current)} />

      {/* Achievement Card */}
      <div className="relative z-10 max-w-sm w-full mx-4">
        {/* Glow ring */}
        <div className={`absolute -inset-4 rounded-3xl bg-gradient-to-r ${current.gradient} opacity-30 blur-xl animate-pulse`} />

        <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Top gradient banner */}
          <div className={`bg-gradient-to-r ${current.gradient} p-6 pb-12 text-center relative`}>
            <button
              onClick={() => dismiss(current)}
              className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
              data-testid="achievement-dismiss"
            >
              <X size={20} />
            </button>

            {/* Animated label */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-white text-xs font-bold uppercase tracking-widest mb-3 animate-bounce">
              <Trophy size={12} />
              Achievement Unlocked
            </div>
          </div>

          {/* Icon floating between banner and content */}
          <div className="flex justify-center -mt-8 relative z-10">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${current.gradient} flex items-center justify-center shadow-lg border-4 border-white`}
              style={{ animation: 'achievement-pop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)' }}
            >
              <Icon size={30} className="text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pt-4 pb-6 text-center">
            <h2 className="text-xl font-extrabold text-gray-900 mb-1">{current.title}</h2>
            <p className="text-sm text-gray-500 leading-relaxed">{current.subtitle}</p>

            <button
              onClick={() => dismiss(current)}
              className={`mt-5 w-full py-3 rounded-xl text-white font-bold text-sm bg-gradient-to-r ${current.gradient} hover:shadow-lg transition-all active:scale-95`}
              data-testid="achievement-continue-btn"
            >
              Keep Going
            </button>
          </div>
        </div>
      </div>

      {/* Keyframe for pop animation */}
      <style>{`
        @keyframes achievement-pop {
          0% { transform: scale(0) rotate(-10deg); }
          50% { transform: scale(1.2) rotate(5deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

// Helper to trigger achievements from anywhere in the app
export function triggerAchievement(type) {
  window.dispatchEvent(new CustomEvent('hibff:achievement', { detail: { type } }));
}

export function triggerCustomAchievement({ icon, title, subtitle, gradient, confettiColors }) {
  window.dispatchEvent(new CustomEvent('hibff:achievement', {
    detail: { custom: { icon, title, subtitle, gradient, confettiColors } },
  }));
}

export default AchievementUnlocked;
